import { getQrCodeUrl } from '@/lib/qrCode'
import {
  BADGE_DESIGN_DPI,
  DEFAULT_BADGE_PRINT_DPI,
  badgeCanvasSizeInPixels,
  resolveElementText,
  resolveMergeValue,
  templateIsDoubleSided,
} from '@/lib/badgeTemplate'
import type {
  BadgeElement,
  BadgeImageElement,
  BadgeMergeTagElement,
  BadgePrintData,
  BadgeShapeElement,
  BadgeTemplate,
  BadgeTextElement,
} from '@/types/badgeDesigner'

type BadgeSide = 'front' | 'back'

interface RenderOptions {
  dpi: number
  flipVertical?: boolean
  flipHorizontal?: boolean
}

interface DrawTextResult {
  lines: string[]
  fontSize: number
}

export interface BadgeZplResult {
  zpl: string
  sidesCount: number
}

const DEFAULT_ZPL_THRESHOLD = 180

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error(`Failed to load image: ${src}`))
    image.src = src
  })

const drawPlaceholder = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  text: string
) => {
  ctx.save()
  ctx.strokeStyle = '#9ca3af'
  ctx.lineWidth = 2
  ctx.strokeRect(x, y, width, height)
  ctx.fillStyle = '#6b7280'
  ctx.font = `bold ${Math.max(12, Math.floor(height / 6))}px Arial`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(text, x + width / 2, y + height / 2, width - 8)
  ctx.restore()
}

const wrapText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] => {
  const source = text.replace(/\r\n/g, '\n')
  const hardLines = source.split('\n')
  const wrapped: string[] = []

  hardLines.forEach((line) => {
    const trimmedLine = line.trim()
    if (!trimmedLine) {
      wrapped.push('')
      return
    }

    const words = trimmedLine.split(/\s+/)
    let currentLine = ''

    words.forEach((word) => {
      const next = currentLine ? `${currentLine} ${word}` : word
      if (ctx.measureText(next).width <= maxWidth) {
        currentLine = next
        return
      }

      if (currentLine) wrapped.push(currentLine)

      if (ctx.measureText(word).width <= maxWidth) {
        currentLine = word
      } else {
        let chunk = ''
        word.split('').forEach((char) => {
          const nextChunk = chunk + char
          if (ctx.measureText(nextChunk).width <= maxWidth) {
            chunk = nextChunk
          } else if (chunk) {
            wrapped.push(chunk)
            chunk = char
          } else {
            wrapped.push(char)
            chunk = ''
          }
        })
        currentLine = chunk
      }
    })

    if (currentLine || wrapped.length === 0) {
      wrapped.push(currentLine)
    }
  })

  return wrapped
}

const fitText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  options: {
    width: number
    height: number
    fontFamily: string
    fontWeight: string
    maxSize: number
    minSize?: number
    lineHeight?: number
  }
): DrawTextResult => {
  const minSize = options.minSize ?? 8
  const lineHeight = options.lineHeight ?? 1.1

  for (let fontSize = options.maxSize; fontSize >= minSize; fontSize -= 1) {
    ctx.font = `${options.fontWeight} ${fontSize}px ${options.fontFamily}`
    const lines = wrapText(ctx, text, options.width)
    const textHeight = lines.length * fontSize * lineHeight
    if (textHeight <= options.height) {
      return { lines, fontSize }
    }
  }

  ctx.font = `${options.fontWeight} ${minSize}px ${options.fontFamily}`
  return { lines: wrapText(ctx, text, options.width), fontSize: minSize }
}

const drawTextElement = (
  ctx: CanvasRenderingContext2D,
  element: BadgeTextElement | BadgeMergeTagElement,
  text: string,
  scale: number
) => {
  const x = element.x * scale
  const y = element.y * scale
  const width = element.width * scale
  const height = element.height * scale
  const maxSize = Math.max(6, element.font_size * scale)
  const { lines, fontSize } = fitText(ctx, text, {
    width,
    height,
    fontFamily: element.font_family || 'Arial',
    fontWeight: element.font_weight || '600',
    maxSize,
  })

  const lineHeight = fontSize * 1.12
  const totalHeight = lines.length * lineHeight
  const startY = y + Math.max(0, (height - totalHeight) / 2)

  ctx.save()
  ctx.fillStyle = element.color || '#111827'
  ctx.font = `${element.font_weight || '600'} ${fontSize}px ${element.font_family || 'Arial'}`
  ctx.textBaseline = 'top'

  if (element.text_align === 'center') {
    ctx.textAlign = 'center'
  } else if (element.text_align === 'right') {
    ctx.textAlign = 'right'
  } else {
    ctx.textAlign = 'left'
  }

  lines.forEach((line, index) => {
    const lineY = startY + index * lineHeight
    const drawX =
      element.text_align === 'center' ? x + width / 2 : element.text_align === 'right' ? x + width : x
    ctx.fillText(line, drawX, lineY, width)
  })

  ctx.restore()
}

const drawShapeElement = (ctx: CanvasRenderingContext2D, element: BadgeShapeElement, scale: number) => {
  const x = element.x * scale
  const y = element.y * scale
  const width = element.width * scale
  const height = element.height * scale

  ctx.save()
  ctx.strokeStyle = element.stroke_color || '#111827'
  ctx.fillStyle = element.fill_color || 'transparent'
  ctx.lineWidth = Math.max(1, (element.stroke_width || 1) * scale)

  if (element.shape_kind === 'line') {
    ctx.beginPath()
    ctx.moveTo(x, y)
    ctx.lineTo(x + width, y + height)
    ctx.stroke()
    ctx.restore()
    return
  }

  if (element.shape_kind === 'ellipse') {
    ctx.beginPath()
    ctx.ellipse(x + width / 2, y + height / 2, Math.abs(width / 2), Math.abs(height / 2), 0, 0, Math.PI * 2)
    if (element.fill_color && element.fill_color !== 'transparent') ctx.fill()
    ctx.stroke()
    ctx.restore()
    return
  }

  if (element.fill_color && element.fill_color !== 'transparent') {
    ctx.fillRect(x, y, width, height)
  }
  ctx.strokeRect(x, y, width, height)
  ctx.restore()
}

const drawImageElement = async (
  ctx: CanvasRenderingContext2D,
  element: BadgeImageElement,
  scale: number
) => {
  const x = element.x * scale
  const y = element.y * scale
  const width = element.width * scale
  const height = element.height * scale

  if (!element.src) {
    drawPlaceholder(ctx, x, y, width, height, 'Image')
    return
  }

  try {
    const image = await loadImage(element.src)
    ctx.drawImage(image, x, y, width, height)
  } catch {
    drawPlaceholder(ctx, x, y, width, height, 'Image')
  }
}

const drawQrElement = async (
  ctx: CanvasRenderingContext2D,
  element: BadgeElement,
  data: BadgePrintData | undefined,
  scale: number
) => {
  const x = element.x * scale
  const y = element.y * scale
  const width = element.width * scale
  const height = element.height * scale
  const qrValue = resolveMergeValue('qr_value', data)

  if (!qrValue) {
    drawPlaceholder(ctx, x, y, width, height, 'QR')
    return
  }

  const imageSize = Math.max(64, Math.round(Math.max(width, height)))
  const qrUrl = getQrCodeUrl(qrValue, { size: imageSize })

  try {
    const qrImage = await loadImage(qrUrl)
    ctx.drawImage(qrImage, x, y, width, height)
  } catch {
    drawPlaceholder(ctx, x, y, width, height, 'QR')
  }
}

const renderElements = async (
  ctx: CanvasRenderingContext2D,
  elements: BadgeElement[],
  data: BadgePrintData | undefined,
  scale: number
) => {
  for (const element of elements) {
    if (element.type === 'text') {
      drawTextElement(ctx, element, resolveElementText(element, data), scale)
      continue
    }

    if (element.type === 'merge_tag') {
      const value = resolveElementText(element, data) || `{{${element.merge_key}}}`
      drawTextElement(ctx, element, value, scale)
      continue
    }

    if (element.type === 'shape') {
      drawShapeElement(ctx, element, scale)
      continue
    }

    if (element.type === 'image') {
      await drawImageElement(ctx, element, scale)
      continue
    }

    if (element.type === 'qr_code') {
      await drawQrElement(ctx, element, data, scale)
    }
  }
}

export const renderBadgeSideCanvas = async (
  template: BadgeTemplate,
  side: BadgeSide,
  data?: BadgePrintData,
  options: RenderOptions = { dpi: BADGE_DESIGN_DPI }
) => {
  const size = badgeCanvasSizeInPixels(template, options.dpi)
  const canvas = document.createElement('canvas')
  canvas.width = size.width
  canvas.height = size.height

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Unable to create badge canvas context.')
  }

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, size.width, size.height)

  const scale = options.dpi / BADGE_DESIGN_DPI
  const elements = side === 'front' ? template.front_elements : template.back_elements
  const orderedElements = [...elements].sort((a, b) => (a.z_index || 0) - (b.z_index || 0))
  await renderElements(ctx, orderedElements, data, scale)

  if (!options.flipVertical && !options.flipHorizontal) return canvas

  const flippedCanvas = document.createElement('canvas')
  flippedCanvas.width = canvas.width
  flippedCanvas.height = canvas.height
  const flippedCtx = flippedCanvas.getContext('2d')
  if (!flippedCtx) return canvas

  flippedCtx.fillStyle = '#ffffff'
  flippedCtx.fillRect(0, 0, flippedCanvas.width, flippedCanvas.height)
  flippedCtx.translate(
    options.flipHorizontal ? flippedCanvas.width : 0,
    options.flipVertical ? flippedCanvas.height : 0
  )
  flippedCtx.scale(options.flipHorizontal ? -1 : 1, options.flipVertical ? -1 : 1)
  flippedCtx.drawImage(canvas, 0, 0)

  return flippedCanvas
}

export const canvasToZpl = (
  canvas: HTMLCanvasElement,
  options: { threshold?: number } = {}
): string => {
  const threshold = options.threshold ?? DEFAULT_ZPL_THRESHOLD
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) throw new Error('Unable to read badge canvas.')

  const { width, height } = canvas
  const imageData = ctx.getImageData(0, 0, width, height).data
  const bytesPerRow = Math.ceil(width / 8)
  const printWidth = bytesPerRow * 8
  const totalBytes = bytesPerRow * height

  const rows: string[] = []

  for (let y = 0; y < height; y += 1) {
    let row = ''

    for (let xByte = 0; xByte < bytesPerRow; xByte += 1) {
      let byte = 0
      for (let bit = 0; bit < 8; bit += 1) {
        const x = xByte * 8 + bit
        if (x >= width) continue
        const pixelIndex = (y * width + x) * 4
        const r = imageData[pixelIndex]
        const g = imageData[pixelIndex + 1]
        const b = imageData[pixelIndex + 2]
        const a = imageData[pixelIndex + 3]
        const luminance = 0.299 * r + 0.587 * g + 0.114 * b
        const isBlack = a > 0 && luminance < threshold
        if (isBlack) {
          byte |= 1 << (7 - bit)
        }
      }
      row += byte.toString(16).padStart(2, '0').toUpperCase()
    }

    rows.push(row)
  }

  const hexData = rows.join('')
  return `^XA^PW${printWidth}^LL${height}^FO0,0^GFA,${totalBytes},${totalBytes},${bytesPerRow},${hexData}^FS^XZ`
}

export const buildBadgeZpl = async (
  template: BadgeTemplate,
  data?: BadgePrintData
): Promise<BadgeZplResult> => {
  const printDpi = Number.isFinite(template.print_dpi) ? template.print_dpi : DEFAULT_BADGE_PRINT_DPI

  const frontCanvas = await renderBadgeSideCanvas(template, 'front', data, {
    dpi: printDpi,
  })
  const frontZpl = canvasToZpl(frontCanvas)

  if (!templateIsDoubleSided(template)) {
    return { zpl: frontZpl, sidesCount: 1 }
  }

  const backCanvas = await renderBadgeSideCanvas(template, 'back', data, {
    dpi: printDpi,
    flipVertical: true,
    flipHorizontal: true,
  })
  const backZpl = canvasToZpl(backCanvas)
  return {
    zpl: `${frontZpl}\n${backZpl}`,
    sidesCount: 2,
  }
}

export const printBadge = async (params: {
  printerUid: string
  template: BadgeTemplate
  data: BadgePrintData
}) => {
  const zpl = await buildBadgeZpl(params.template, params.data)
  const { sendToPrinter } = await import('@/lib/browserPrint')
  await sendToPrinter(params.printerUid, zpl.zpl)
  return zpl
}
